// Phase 31 Plan 31-04 Task 1 — KnownOpponentRunesRow.
//
// Sub-component of AffinityChipRow. Renders below the affinity chip row only
// when the caller has played Scrivener's Quill in Read mode and the server
// surfaced 2 opponent letters via `your_read_revealed_letters`. The parent
// (AffinityChipRow) gates the mount on `letters.length === 2`; this component
// assumes the caller passed a tuple of exactly 2 entries.
//
// Visual contract (UI-SPEC §1 row 6 + §3 token reuse + §6 i18n):
// - 24×24 mini chips on `--color-surface-deep` (darker than AffinityChip's
//   `--color-surface-mid` to reinforce the "secondary information" hierarchy).
// - Fredoka 700 / `--font-size-16` glyph.
// - Quill icon overlay top-right of each chip, tinted `--color-cta-primary`
//   (Sun-Yellow). The atlas frame `pi_quill` isn't directly DOM-renderable,
//   so we fall back to the Unicode ✒ glyph per plan §<action> step 3.
// - Caption label "Their runes (known)" in Manrope 600 / `--font-size-11`.
// - Inert — pure <div>/<span> spans; no buttons; no tap surface (T-31-01).
//
// B12 firewall (T-31-01): consumes ONLY the parent's `letters` prop
// (length-2 tuple from `your_read_revealed_letters` — a caller-only field
// guaranteed by the backend's projectPlayerView). This component has NO
// awareness of `opponent_public` or any opponent.* fields; the firewall holds
// at compile time (the prop type is `readonly [string, string]`).

import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

export interface KnownOpponentRunesRowProps {
  readonly letters: readonly [string, string]
}

export function KnownOpponentRunesRow({
  letters,
}: KnownOpponentRunesRowProps): JSX.Element {
  const intl = useIntl()
  return (
    <section
      data-screen-region="known-opponent-runes"
      aria-label={intl.formatMessage({
        id: 'match.affinity.known_opponent_runes_label',
      })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1)',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-family-body)',
          fontWeight: 600,
          fontSize: 'var(--font-size-11)',
          lineHeight: 1.4,
          color: 'var(--color-text-secondary)',
        }}
      >
        <FormattedMessage id="match.affinity.known_opponent_runes_label" />
      </span>
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        {letters.map((letter, idx) => (
          // Composite key: letter + idx keeps stability under duplicate
          // letters in the read-revealed tuple (e.g. ['A','A']); a
          // letter-only key would collide and a pure-idx key drops affordance.
          // biome-ignore lint/suspicious/noArrayIndexKey: composite key with stable letter prefix; bounded length-2 tuple
          <MiniChip key={`known-opp-${letter}-${idx}`} letter={letter} />
        ))}
      </div>
    </section>
  )
}

function MiniChip({ letter }: { readonly letter: string }): JSX.Element {
  return (
    <div
      aria-hidden="true"
      data-known-opp-letter={letter.toUpperCase()}
      style={{
        position: 'relative',
        width: 24,
        height: 24,
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-deep)',
        border: '2px solid var(--color-surface-stroke)',
        color: 'var(--color-inkwell)',
        fontFamily: 'var(--font-family-display)',
        fontWeight: 700,
        fontSize: 'var(--font-size-16)',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {letter.toUpperCase()}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -4,
          right: -4,
          fontSize: 8,
          color: 'var(--color-cta-primary)',
        }}
      >
        ✒
      </span>
    </div>
  )
}
